import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Box,
  CollectionPreferences,
  Container,
  Header,
  Pagination,
  Table,
} from '@amzn/awsui-components-react-v3';
import _ from 'lodash';
import React, { ReactElement, useState } from 'react';
import { CollectionPreferencesProps } from '@amzn/awsui-components-react-v3/uxdg';
import { useTranslation } from 'react-i18next';

type GetCycleCountHistoryResult = {
  checked: number;
  date: string;
  type: string;
  missing: number;
  newlyAdded: number;
  user: string;
};

export function renderField(fieldName: string | number) {
  return (e: GetCycleCountHistoryResult): string | number => {
    return _.get(e, fieldName, '-');
  };
}

const CycleCountHistoryDetail = (props: {
  cycleCountHistory: any;
  loadingCycleCountHistory: boolean;
}): ReactElement => {
  const { cycleCountHistory, loadingCycleCountHistory } = props;
  const { t } = useTranslation();

  const columnDefinitions = [
    {
      id: 'date',
      header: 'Date',
      cell: (item: GetCycleCountHistoryResult) => item.date || '-',
      sortingField: 'date',
    },
    {
      id: 'type',
      header: 'Type',
      cell: (item: GetCycleCountHistoryResult) => item.type || '-',
      sortingField: 'type',
    },
    {
      id: 'user',
      header: 'User',
      cell: (item: GetCycleCountHistoryResult) => item.user || '-',
      sortingField: 'user',
    },
    {
      id: 'checked',
      header: 'Counted',
      cell: renderField('checked'),
      sortingField: 'checked',
    },
    {
      id: 'missing',
      header: 'Missing',
      cell: renderField('missing'),
      sortingField: 'missing',
    },
    {
      id: 'newlyAdded',
      header: 'New',
      cell: renderField('newlyAdded'),
      sortingField: 'newlyAdded',
    },
  ];

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 10,
    visibleContent: ['date', 'type', 'user', 'checked', 'missing', 'newlyAdded'],
  });

  const { items, collectionProps, paginationProps } = useCollection(cycleCountHistory, {
    pagination: { pageSize: preferences.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0], isDescending: true } },
  });

  return (
    <div>
      {items.length > 0 && (
        <Table
          {...collectionProps}
          columnDefinitions={columnDefinitions}
          items={items}
          header={<Header> {t('Cycle count history')}</Header>}
          visibleColumns={preferences.visibleContent}
          pagination={
            <Pagination
              {...paginationProps}
              ariaLabels={{
                nextPageLabel: t('Next page'),
                previousPageLabel: t('Previous page'),
                pageLabel: (pageNum) => t(`Page ${pageNum} of all pages`),
              }}
            />
          }
          loading={loadingCycleCountHistory}
          preferences={
            <CollectionPreferences
              title={t('Preferences')}
              onConfirm={({ detail }) => setPreferences(detail)}
              confirmLabel={t('Confirm')}
              cancelLabel={t('Cancel')}
              preferences={preferences}
              pageSizePreference={{
                title: t('Select page size'),
                options: [
                  { value: 5, label: t('5 items') },
                  { value: 10, label: t('10 items') },
                  { value: 20, label: t('20 items') },
                  { value: 50, label: t('50 items') },
                  { value: 100, label: t('100 items') },
                ],
              }}
              visibleContentPreference={{
                title: t('Select visible content'),
                options: [
                  {
                    label: t('Main distribution properties'),
                    options: [
                      {
                        id: 'date',
                        label: t('Date'),
                        editable: false,
                      },
                      { id: 'user', label: t('User') },
                      { id: 'checked', label: t('Counted') },
                      { id: 'missing', label: t('Missing') },
                      {
                        id: 'newlyAdded',
                        label: t('New'),
                      },
                    ],
                  },
                ],
              }}
            />
          }
        />
      )}
      {items.length === 0 && (
        <Container header={<Header> {t('Cycle count history')}</Header>}>
          <Box color="text-status-inactive" textAlign="center">
            {t('No Cycle count history to display')}
          </Box>
        </Container>
      )}
    </div>
  );
};

export default CycleCountHistoryDetail;
